/* eslint-disable import/prefer-default-export */
import _ from 'lodash';

import OhsUserLocalServices from 'user/OhsUserLocalServices';
import { OhsUser } from 'user/OhsUser';
import { getWorkplaceOptions } from 'user/OhsUserServices';
import { stringToHash } from 'global-services/utils/OhsUtility';

import { OhsFormattedParamList, OhsListParams, ParamTypes } from '../OhsRegisterModels';
import { globalFilterDefaults } from '../OhsRegisterConstants';
import { formatArrayBinaryToString } from '../OhsBinaryChecker';

// remove similarities of payload and default vals
const removeMatchingDefaults = <T extends Record<string, any>>(
  payload: T,
  defaults: Partial<T>
): any => {
  // check if equal, if array, sort before comparing
  const deepEqualWithSortedArrays = (a: any, b: any): boolean => {
    if (a === b) return true;

    if (Array.isArray(a) && Array.isArray(b)) {
      const sortedA = [...a].sort();
      const sortedB = [...b].sort();
      return (
        sortedA.length === sortedB.length &&
        sortedA.every((val, i) => deepEqualWithSortedArrays(val, sortedB[i]))
      );
    }

    // for sort type mismatch
    if (
      (typeof a === 'object' && typeof b === 'string') ||
      (typeof a === 'string' && typeof b === 'object')
    ) {
      if (typeof a === 'object') return JSON.stringify(a) === b;
      if (typeof b === 'object') return JSON.stringify(b) === a;
    }

    if (typeof a !== typeof b) return false;

    if (typeof a === 'object' && typeof b === 'object' && a && b) {
      const keysA = Object.keys(a);
      const keysB = Object.keys(b);
      if (keysA.length !== keysB.length) return false;
      return keysA.every((key) => deepEqualWithSortedArrays(a[key], b[key]));
    }

    return false;
  };

  const uniqueProps = Object.fromEntries(
    Object.entries(payload).filter(
      ([key, value]) => !deepEqualWithSortedArrays(value, defaults[key as keyof T])
    )
  );

  // remove unnecessary values
  if (uniqueProps?.[ParamTypes.Page] === 1) delete uniqueProps[ParamTypes.Page];
  delete uniqueProps.count;

  return uniqueProps;
};

export const formatListPayloadToParams = (
  payload: Partial<OhsFormattedParamList> & {
    filter?: any;
  },
  defaultFilter: any,
  props?: {
    withWorkplace?: boolean;
    moduleOptions?: string[];
    categoryOptions?: string[];
    subCategoryOptions?: string[];
    severityOptions?: string[];
    statusOptions?: string[];
    sortOptions?: {
      filter: { name: string; value: string }[];
      search?: { name: string; value: string }[];
    };
    sort?: string;
    allocatedToOrg?: boolean;
    allocatedToWorkplace?: boolean;
    notAllocated?: boolean;
    workplaceOwned?: boolean;
  }
) => {
  const user = OhsUserLocalServices.getLocalOhsUser() as OhsUser;

  let tempPayload: any = {};
  let newPayload: any = {};

  // merge filters into 1 object, for search payload's nested filter
  tempPayload = payload?.filter ? { ...payload?.filter, ...payload } : payload;
  // remove similarities
  tempPayload = removeMatchingDefaults(tempPayload, {
    ...globalFilterDefaults,
    ...defaultFilter,
  });

  // ============================== FUNCS ==============================
  const returnValue = (key: keyof OhsListParams) => {
    let val = tempPayload?.[key] || null;

    if (!_.isNil(val)) {
      if (
        key === ParamTypes.DateCompleted ||
        key === ParamTypes.DateSignedoff ||
        key === ParamTypes.DateCreated ||
        key === ParamTypes.DateIdentified ||
        key === ParamTypes.DateIssued ||
        key === ParamTypes.DateExpiry ||
        (key === ParamTypes.DateOccurred && typeof val === 'object') ||
        (key === ParamTypes.DateDue && typeof val === 'object')
      ) {
        // remove value if one part is missing
        if (!val?.start || !val?.end) {
          val = undefined;
          return;
        }
      }
      if (typeof val !== 'number' && typeof val !== 'string') {
        val = JSON.stringify(val);
      }
      newPayload = {
        ...newPayload,
        [key]: val,
      };
    }
  };
  const returnBooleanValue = (key: keyof OhsListParams) => {
    const val = tempPayload?.[key] ?? null;
    if (!_.isNil(val)) {
      const newVal = val === true ? '1' : '0';
      newPayload = {
        ...newPayload,
        [key]: newVal,
      };
    }
  };

  // ============================== NON FILTER RELATED ========================
  returnValue(ParamTypes.Tab);
  returnBooleanValue(ParamTypes.FolderView);

  // ============================== BASIC PARAMS ==============================
  returnBooleanValue(ParamTypes.Archived);
  returnBooleanValue(ParamTypes.ShowSignedOff);
  returnValue(ParamTypes.Sort);
  returnValue(ParamTypes.DateDue);
  returnValue(ParamTypes.DateDueType);
  returnValue(ParamTypes.DateCompleted);
  returnValue(ParamTypes.DateSignedoff);
  returnValue(ParamTypes.DateCreated);
  returnValue(ParamTypes.DateOccurred);
  returnValue(ParamTypes.DateIdentified);
  returnValue(ParamTypes.DateIssued);
  returnValue(ParamTypes.DateExpiry);
  returnBooleanValue(ParamTypes.AllocatedToOrg);
  returnBooleanValue(ParamTypes.AllocatedToWorkplace);
  returnBooleanValue(ParamTypes.Confidential);
  returnBooleanValue(ParamTypes.NotAllocated);
  returnBooleanValue(ParamTypes.WorkplaceOwned);
  returnBooleanValue(ParamTypes.PropOrEnvDamage);
  returnBooleanValue(ParamTypes.Leave);
  returnBooleanValue(ParamTypes.IncludeTierNull);
  returnBooleanValue(ParamTypes.ReviewScheduled);
  returnBooleanValue(ParamTypes.Copied);
  returnBooleanValue(ParamTypes.CopiedVersionUpdated);
  returnBooleanValue(ParamTypes.NotCopied);
  returnBooleanValue(ParamTypes.InProgress);
  returnValue(ParamTypes.Category);
  returnValue(ParamTypes.Subcategory);
  returnValue(ParamTypes.TemplateTypes);
  // ============================== SEARCH ==============================
  returnValue(ParamTypes.SearchKey);
  returnBooleanValue(ParamTypes.SubstringSearch);
  returnBooleanValue(ParamTypes.SkipOrgRecords);

  // ============================== PAGINATION ==============================
  returnValue(ParamTypes.Page);
  returnValue(ParamTypes.Next);

  // ============================== BINARIES ==============================
  // if workplaces is in payload, compress and add hash
  if (tempPayload?.[ParamTypes.Workplaces] && props?.withWorkplace) {
    const getWorkplaceLists = getWorkplaceOptions(user?.configs);
    const workplaceOptions: string[] = getWorkplaceLists.map((list: any) => list.id).sort();
    const stringOptions = JSON.stringify(workplaceOptions);
    const hashedOptions = stringToHash(stringOptions);

    newPayload[ParamTypes.Workplaces] = formatArrayBinaryToString(
      tempPayload?.[ParamTypes.Workplaces] || [],
      workplaceOptions
    );
    newPayload[ParamTypes.WorkplacesHash] = hashedOptions;
  }

  // if modules is in payload, compress and add hash
  if (tempPayload?.[ParamTypes.Modules] && props?.moduleOptions) {
    const sortedOptions = props?.moduleOptions.sort();
    const stringOptions = JSON.stringify(sortedOptions);
    const hashedOptions = stringToHash(stringOptions);

    newPayload[ParamTypes.Modules] = formatArrayBinaryToString(
      tempPayload?.[ParamTypes.Modules] || [],
      props?.moduleOptions
    );
    newPayload[ParamTypes.ModulesHash] = hashedOptions;
  }

  // if severities is in payload, compress and add hash
  if (tempPayload?.[ParamTypes.Severities] && props?.severityOptions) {
    const sortedOptions = props?.severityOptions.sort();
    const stringOptions = JSON.stringify(sortedOptions);
    const hashedOptions = stringToHash(stringOptions);

    newPayload[ParamTypes.Severities] = formatArrayBinaryToString(
      tempPayload?.[ParamTypes.Severities] || [],
      props?.severityOptions
    );
    newPayload[ParamTypes.SeveritiesHash] = hashedOptions;
  }

  // if categories is in payload, compress and add hash
  if (tempPayload?.[ParamTypes.Categories] && props?.categoryOptions) {
    const sortedOptions = props?.categoryOptions.sort();
    const stringOptions = JSON.stringify(sortedOptions);
    const hashedOptions = stringToHash(stringOptions);

    newPayload[ParamTypes.Categories] = formatArrayBinaryToString(
      tempPayload?.[ParamTypes.Categories] || [],
      props?.categoryOptions
    );
    newPayload[ParamTypes.CategoriesHash] = hashedOptions;
  }

  // if subCategories is in payload, compress and add hash
  if (tempPayload?.[ParamTypes.SubCategories] && props?.subCategoryOptions) {
    const sortedOptions = props?.subCategoryOptions.sort();
    const stringOptions = JSON.stringify(sortedOptions);
    const hashedOptions = stringToHash(stringOptions);

    newPayload[ParamTypes.SubCategories] = formatArrayBinaryToString(
      tempPayload?.[ParamTypes.SubCategories] || [],
      props?.subCategoryOptions
    );
    newPayload[ParamTypes.SubCategoriesHash] = hashedOptions;
  }

  // if statuses is in payload, compress and add hash
  if (tempPayload?.[ParamTypes.Status] && props?.statusOptions) {
    const sortedOptions = props?.statusOptions.sort();
    const stringOptions = JSON.stringify(sortedOptions);
    const hashedOptions = stringToHash(stringOptions);

    newPayload[ParamTypes.Status] = formatArrayBinaryToString(
      tempPayload?.[ParamTypes.Status] || [],
      props?.statusOptions
    );
    newPayload[ParamTypes.StatusHash] = hashedOptions;
  }

  // ============================== CONDITIONAL ==============================
  // options for filter and search are not always the same
  // if sort value does not exist in the current target(filter or search), use the first option
  if (props?.sortOptions && tempPayload?.[ParamTypes.Sort]) {
    const sortValue = tempPayload?.[ParamTypes.Sort];
    const withSearch = !!tempPayload?.[ParamTypes.SearchKey];
    const target = withSearch ? 'search' : 'filter';
    const options = props?.sortOptions[target];
    const isValid = (options || []).find(
      (item) =>
        item.value === (typeof sortValue === 'string' ? sortValue : JSON.stringify(sortValue))
    );

    // if sort value does not exist in the sort options, use the first option
    if (!isValid) {
      if (withSearch && options) {
        newPayload[ParamTypes.Sort] = options[0].value;
      } else {
        delete newPayload[ParamTypes.Sort];
      }
    }
  }

  return newPayload;
};
